import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Check, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface ScenarioCardProps {
  id?: string;
  title: string;
  description: string;
  scenarioType: string;
  orderIndex: number;
  isEditing?: boolean;
  onSave: (data: { title: string; description: string }) => void;
  onEdit?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const ScenarioCard = ({
  title,
  description,
  scenarioType,
  orderIndex,
  isEditing: externalIsEditing = false,
  onSave,
  onEdit,
  className,
  style,
}: ScenarioCardProps) => {
  const [isEditing, setIsEditing] = useState(externalIsEditing);
  const [editTitle, setEditTitle] = useState(title);
  const [editDescription, setEditDescription] = useState(description);

  const handleEdit = () => {
    setIsEditing(true);
    onEdit?.();
  };

  const handleSave = () => {
    onSave({ title: editTitle, description: editDescription });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(title);
    setEditDescription(description);
    setIsEditing(false);
  };

  const getTypeLabel = () => {
    if (scenarioType === "baseline") {
      return "Baseline";
    }
    // For "option" type, use the title as label
    return title || "Option";
  };

  const getTypeColor = () => {
    if (scenarioType === "baseline") {
      return "bg-muted text-muted-foreground border-border";
    }
    // For "option" type, alternate between primary and accent based on orderIndex
    return orderIndex % 2 === 0
      ? "bg-primary/10 text-primary border-primary/20"
      : "bg-accent/10 text-accent-foreground border-accent/20";
  };

  return (
    <Card className={cn("relative transition-all hover:shadow-md animate-fade-in", className)} style={style}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-2">
            <div
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border",
                getTypeColor()
              )}
            >
              {getTypeLabel()}
            </div>
            {isEditing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Titel der Option"
                className="font-semibold"
              />
            ) : (
              <CardTitle className="text-xl">{title}</CardTitle>
            )}
          </div>
          {!isEditing && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleEdit}
              className="h-11 w-11 shrink-0"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <>
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Beschreibung der Option"
              rows={4}
              className="resize-none"
            />
            <div className="flex gap-3 mt-4">
              <Button onClick={handleSave} size="sm" className="flex-1 h-11">
                <Check className="h-4 w-4 mr-2" />
                Speichern
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
                className="flex-1 h-11"
              >
                <X className="h-4 w-4 mr-2" />
                Abbrechen
              </Button>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground leading-relaxed">
            {description || "Klicke auf Bearbeiten, um eine Beschreibung hinzuzufügen"}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
